import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { H1, Body, Button, DataTable, Modal } from '../components/ui';
import { Column } from '../components/ui/DataTable';
import { EyeIcon, PencilIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useChildren, useDeleteChild } from '../hooks/api';
import { useHasPermission } from '../stores/authStore';
import { useModal } from '../stores/uiStore';
import { Child, ChildFilters } from '../types/api';
import { formatDateShort } from '../utils/date';

const Children: React.FC = () => {
  const navigate = useNavigate();
  const [filters, _setFilters] = useState<ChildFilters>({
    limit: 100,
    skip: 0,
  });
  
  // Permissions
  const canCreateChild = useHasPermission('child.create');
  const canUpdateChild = useHasPermission('child.update');
  const _canDeleteChild = useHasPermission('child.delete');
  
  // Fetch children data
  const { data, isLoading, error } = useChildren(filters);
  const deleteChildMutation = useDeleteChild();
  
  // Modal for delete confirmation
  const deleteModal = useModal('delete-child');
  const [childToDelete, setChildToDelete] = useState<Child | null>(null);

  // Define table columns
  const columns: Column<Child>[] = [
    {
      key: 'name',
      title: 'Name',
      sortable: true,
      filterable: true,
      render: (value, row) => (
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
            <span className="text-primary-600 font-medium text-sm">
              {row.name[0]}{row.surname[0]}
            </span>
          </div>
          <div>
            <span className="font-medium">{row.name} {row.surname}</span>
          </div>
        </div>
      ),
    },
    {
      key: 'age',
      title: 'Age',
      sortable: true,
      width: '80px',
    },
    {
      key: 'birthdate',
      title: 'Birth Date',
      sortable: true,
      render: (value) => formatDateShort(value as string),
    },
    {
      key: 'gender',
      title: 'Gender',
      sortable: true,
      filterable: true,
      width: '80px',
      render: (value) => {
        const gender = value as string;
        return gender === 'M' ? 'Male' : gender === 'F' ? 'Female' : 'Other';
      },
    },
  ];

  const handleRowClick = (child: Child) => {
    navigate(`/children/${child.id}`);
  };

  const handleRowSelect = (selectedChildren: Child[]) => {
    console.log('Selected children:', selectedChildren);
  };

  const _handleDeleteChild = (child: Child) => {
    setChildToDelete(child);
    deleteModal.open();
  };

  const confirmDelete = async () => {
    if (childToDelete) {
      await deleteChildMutation.mutateAsync(childToDelete.id);
      deleteModal.close();
      setChildToDelete(null);
    }
  };

  const actions = [
    {
      label: 'View',
      icon: EyeIcon,
      onClick: (child: Child) => navigate(`/children/${child.id}`),
      variant: 'primary' as const,
    },
    ...(canUpdateChild ? [{
      label: 'Edit',
      icon: PencilIcon,
      onClick: (child: Child) => navigate(`/children/${child.id}/edit`),
      variant: 'secondary' as const,
    }] : []),
  ];

  if (error) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <Body className="text-red-600 dark:text-red-400">
            Failed to load children. Please try refreshing the page.
          </Body>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <H1 className="mb-2">Children</H1>
          <Body className="text-gray-600 dark:text-gray-400">
            View and manage all children in your care.
          </Body>
        </div>
        {canCreateChild && (
          <Button
            variant="primary"
            onClick={() => navigate('/children/new')}
            className="self-start sm:self-auto"
          >
            <PlusIcon className="w-5 h-5 mr-2" />
            Add Child
          </Button>
        )}
      </div>

      <DataTable
        data={data?.items || []}
        columns={columns}
        onRowClick={handleRowClick}
        onRowSelect={handleRowSelect}
        actions={actions}
        loading={isLoading}
        searchable={true}
        filterable={true}
        sortable={true}
        pagination={true}
        pageSize={filters.limit || 100}
        emptyMessage="No children found"
      />

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModal.isOpen}
        onClose={deleteModal.close}
        title="Permanently delete child record"
        size="md"
        actions={[
          {
            label: 'Cancel',
            onClick: deleteModal.close,
            variant: 'secondary',
          },
          {
            label: 'Delete permanently',
            onClick: confirmDelete,
            variant: 'danger',
            loading: deleteChildMutation.isPending,
          },
        ]}
      >
        <div className="space-y-3">
          <Body>
            You're about to permanently delete{' '}
            <strong>{childToDelete?.name} {childToDelete?.surname}</strong>.
            This is a hard cascade and is irreversible.
          </Body>
          <div className="rounded-lg border border-red-200 dark:border-red-900/40 bg-red-50/60 dark:bg-red-900/10 p-3 text-sm text-red-900 dark:text-red-200">
            <p className="font-semibold mb-1.5">This will permanently remove:</p>
            <ul className="list-disc pl-5 space-y-0.5 text-xs">
              <li>The child's profile and learning periods</li>
              <li>Every observation and assessment about this child</li>
              <li>All photos and videos attached to those observations</li>
              <li>Recorded parental consents and pending guardian invitations</li>
              <li>The links between this child and their guardians</li>
            </ul>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Audit-log entries referencing this child are kept (by UUID only) so the
            access trail survives, as required under POPIA s17. Backups within the
            retention window may still hold the data until they expire — see the{' '}
            <a
              href="/docs/security/popia"
              target="_blank"
              rel="noreferrer"
              className="underline hover:no-underline"
            >
              POPIA Compliance
            </a>{' '}
            doc.
          </p>
        </div>
      </Modal>
    </div>
  );
};

export default Children;
